import { useMemo, useState } from "react"
import { BsGlobe, BsShieldLock, BsTranslate } from "react-icons/bs"

import { useTranslation } from "react-i18next"

import { useProps } from "contexts/PropsContext"

import { AdminLocale, LOCALE_NAMES } from "constants/locales"

import { getUtcOffset } from "util/date"

import ChevronIcon from "../../assets/icons/chevron-icon.svg"
import person from "../../assets/images/person.png"
import { LanguageModal } from "../modals/LanguageModal"
import { TimezoneModal } from "../modals/TimezoneModal"

interface UserDropdownProps {
  onOpenTwoFactorSetup: () => void
  onOpenTwoFactorManage: () => void
}

export const UserDropdown = ({ onOpenTwoFactorSetup, onOpenTwoFactorManage }: UserDropdownProps) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const { user, onUserLogout, language, timezone } = useProps()

  const [open, setOpen] = useState(false)
  const [twoFaExpanded, setTwoFaExpanded] = useState(false)
  const [languageModalOpen, setLanguageModalOpen] = useState(false)
  const [timezoneModalOpen, setTimezoneModalOpen] = useState(false)

  const currentOffset = useMemo(() => getUtcOffset(timezone), [timezone])

  const handleOpenLanguage = () => {
    setOpen(false)
    setLanguageModalOpen(true)
  }

  const handleOpenTimezone = () => {
    setOpen(false)
    setTimezoneModalOpen(true)
  }

  const handleOpenTwoFactorManage = () => {
    setOpen(false)
    onOpenTwoFactorManage()
  }

  const handleOpenTwoFactorSetup = () => {
    setOpen(false)
    onOpenTwoFactorSetup()
  }

  const btnClass =
    "flex items-center gap-2 w-full px-2 py-1.5 rounded text-left text-xs font-medium transition hover:bg-gray-100 dark:hover:bg-[#2a2b39]"

  return (
    <>
      <div className="relative">
        <button
          onClick={() => setOpen((prev) => !prev)}
          className="flex items-center gap-2 rounded-full px-[6px] py-[6px] border bg-white dark:bg-[#21222E] border-gray-200 dark:border-[rgba(255,255,255,0.1)] hover:bg-gray-100 dark:hover:bg-[#2a2b39] text-black dark:text-white"
        >
          <div className="rounded-full overflow-hidden">
            <img src={person} alt="Person image" className="w-9 h-9" />
          </div>
          <span className="hidden lg:inline text-xl font-medium capitalize">{user?.username}</span>
          <img src={ChevronIcon} alt="Chevron icon" className={`w-6 h-6 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {open && (
          <div className="absolute right-0 mt-2 w-52 z-40 rounded-lg shadow-lg border text-sm p-2 bg-white dark:bg-[#21222E] text-black dark:text-white border-gray-200 dark:border-[rgba(255,255,255,0.1)]">
            <div className="flex flex-col gap-0.5">
              {/* Language button */}
              <button className={btnClass} onClick={handleOpenLanguage}>
                <BsTranslate className="w-3.5 h-3.5" />
                {t("settings.language")}
                <span className="ml-auto opacity-50 text-[11px]">{LOCALE_NAMES[language as AdminLocale] ?? language}</span>
              </button>

              {/* Timezone button */}
              <button className={btnClass} onClick={handleOpenTimezone}>
                <BsGlobe className="w-3.5 h-3.5" />
                {t("settings.timezone")}
                <span className="ml-auto opacity-50 text-[11px]">{currentOffset || "UTC"}</span>
              </button>

              <div className="border-t my-1 border-gray-200 dark:border-white/10" />

              {/* 2FA collapsible */}
              <div>
                <button onClick={() => setTwoFaExpanded((prev) => !prev)} className={`${btnClass} justify-between`}>
                  <span className="flex items-center gap-2">
                    <BsShieldLock className="w-3.5 h-3.5" />
                    {t("auth.twoFa.title")}
                  </span>
                  <img src={ChevronIcon} alt="" className={`w-4 h-4 transition-transform ${twoFaExpanded ? "rotate-180" : ""}`} />
                </button>

                {twoFaExpanded && (
                  <div className="mt-1 px-2 flex flex-col gap-1.5 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="opacity-70">{t("auth.twoFa.statusLabel")}</span>
                      <span className={user?.use_two_fa ? "text-green-500 font-medium" : "text-red-400 font-medium"}>
                        {user?.use_two_fa ? t("common.enabled") : t("common.disabled")}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="opacity-70">{t("auth.twoFa.actionLabel")}</span>
                      {user?.use_two_fa ? (
                        <button className="text-red-400 underline cursor-pointer" onClick={handleOpenTwoFactorManage}>
                          {t("common.disable")}
                        </button>
                      ) : (
                        <button className="text-green-500 underline cursor-pointer" onClick={handleOpenTwoFactorSetup}>
                          {t("common.enable")}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="border-t my-1 border-gray-200 dark:border-white/10" />

              {/* Logout */}
              <button onClick={onUserLogout} className={`${btnClass} justify-center`}>
                {t("auth.logout")}
              </button>
            </div>
          </div>
        )}
      </div>

      <LanguageModal open={languageModalOpen} onClose={() => setLanguageModalOpen(false)} />
      <TimezoneModal open={timezoneModalOpen} onClose={() => setTimezoneModalOpen(false)} />
    </>
  )
}
